import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileWarning,
  Search,
  AlertTriangle,
  AlertCircle,
  Clock,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Mail,
  ExternalLink,
  FileText,
  Shield,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { trpc } from "@/lib/trpc";

// Sentinel "ALL" car Radix Select interdit value="" (crashe React).
const ALL = "ALL" as const;
type DocStatus = "PENDING" | "APPROVED" | "REJECTED" | "EXPIRED" | typeof ALL;
type DocType = "RC_PRO" | "DECENNALE" | "KBIS" | typeof ALL;

function getUrgencyInfo(expiresAt: string | null, status: string) {
  if (!expiresAt || status !== "APPROVED") return { label: "N/A", color: "bg-muted text-muted-foreground" };
  const now = new Date();
  const exp = new Date(expiresAt);
  const diff = exp.getTime() - now.getTime();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

  if (days < 0) return { label: "EXPIRÉ", color: "bg-destructive text-destructive-foreground" };
  if (days <= 30) return { label: `${days}j`, color: "bg-orange-500 text-white" };
  if (days <= 90) return { label: `${days}j`, color: "bg-yellow-500 text-white" };
  return { label: "OK", color: "bg-emerald-500 text-white" };
}

function formatDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

function docTypeLabel(type: string) {
  const map: Record<string, string> = {
    RC_PRO: "RC Pro",
    DECENNALE: "Décennale",
    KBIS: "Kbis",
    ATTESTATION_URSSAF: "URSSAF",
    CARTE_PRO: "Carte Pro",
  };
  return map[type] || type;
}

export default function DocumentsExpiration() {
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [typeFilter, setTypeFilter] = useState<DocType>(ALL);
  const [statusFilter, setStatusFilter] = useState<DocStatus>(ALL);

  const defaultExpiry = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
  const [expiryBefore, setExpiryBefore] = useState(defaultExpiry);

  const { data, isLoading } = trpc.admin.travauxbtp.listAllDocuments.useQuery({
    page,
    limit: 30,
    type: typeFilter === ALL ? undefined : typeFilter,
    status: statusFilter === ALL ? undefined : statusFilter,
    expiryBefore: expiryBefore || undefined,
    search: search || undefined,
  });

  const sendEmailMutation = trpc.admin.travauxbtp.sendArtisanEmail.useMutation({
    onSuccess: () => toast.success("Email de rappel envoyé"),
    onError: (err) => toast.error(err.message || "Erreur lors de l'envoi"),
  });

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const stats = data?.stats;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Documents — Suivi Expiration</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Surveillance des documents artisans avec alertes d'expiration
          </p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-destructive/50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-destructive/10">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Expirés</p>
                  <p className="text-2xl font-bold text-destructive">
                    {isLoading ? "…" : stats?.expired ?? 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-orange-500/50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/10">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Expirent sous 30j</p>
                  <p className="text-2xl font-bold text-orange-500">
                    {isLoading ? "…" : stats?.expiringIn30Days ?? 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-yellow-500/50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Expirent sous 90j</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {isLoading ? "…" : stats?.expiringIn90Days ?? 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Recherche</label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Nom, email, entreprise…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="h-9"
                  />
                  <Button size="sm" variant="outline" onClick={handleSearch}>
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="w-[140px]">
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Type</label>
                <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v as DocType); setPage(1); }}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Tous</SelectItem>
                    <SelectItem value="RC_PRO">RC Pro</SelectItem>
                    <SelectItem value="DECENNALE">Décennale</SelectItem>
                    <SelectItem value="KBIS">Kbis</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="w-[140px]">
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Statut</label>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as DocStatus); setPage(1); }}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Tous</SelectItem>
                    <SelectItem value="APPROVED">Approuvé</SelectItem>
                    <SelectItem value="PENDING">En attente</SelectItem>
                    <SelectItem value="EXPIRED">Expiré</SelectItem>
                    <SelectItem value="REJECTED">Rejeté</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="w-[160px]">
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Expire avant le</label>
                <Input
                  type="date"
                  value={expiryBefore}
                  onChange={(e) => { setExpiryBefore(e.target.value); setPage(1); }}
                  className="h-9"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium">Artisan</th>
                    <th className="text-left px-4 py-3 font-medium">Type</th>
                    <th className="text-left px-4 py-3 font-medium">Statut</th>
                    <th className="text-left px-4 py-3 font-medium">Soumission</th>
                    <th className="text-left px-4 py-3 font-medium">Expiration</th>
                    <th className="text-left px-4 py-3 font-medium">Urgence</th>
                    <th className="text-right px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Chargement…</td></tr>
                  ) : !data?.items?.length ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Aucun document trouvé</td></tr>
                  ) : (
                    data.items.map((doc: any) => {
                      const urgency = getUrgencyInfo(doc.expiresAt, doc.status);
                      const artisan = doc.ArtisanProfile;
                      const artisanName = artisan
                        ? `${artisan.User?.firstName || ""} ${artisan.User?.lastName || ""}`.trim() || artisan.companyName || "—"
                        : "—";

                      return (
                        <tr key={doc.id} className="border-b hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3">
                            <div>
                              <span className="font-medium">{artisanName}</span>
                              {artisan?.companyName && (
                                <span className="block text-xs text-muted-foreground">{artisan.companyName}</span>
                              )}
                              {artisan?.User?.email && (
                                <span className="block text-xs text-muted-foreground">{artisan.User.email}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="text-xs">{docTypeLabel(doc.type)}</Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              variant="outline"
                              className={
                                doc.status === "APPROVED" ? "border-emerald-500 text-emerald-600" :
                                doc.status === "PENDING" ? "border-yellow-500 text-yellow-600" :
                                doc.status === "REJECTED" ? "border-destructive text-destructive" :
                                ""
                              }
                            >
                              {doc.status === "APPROVED" ? "Approuvé" :
                               doc.status === "PENDING" ? "En attente" :
                               doc.status === "REJECTED" ? "Rejeté" : doc.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{formatDate(doc.uploadedAt || doc.createdAt)}</td>
                          <td className="px-4 py-3 text-muted-foreground">{formatDate(doc.expiresAt)}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${urgency.color}`}>
                              {urgency.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {doc.fileUrl && (
                                <Button size="sm" variant="ghost" asChild className="h-7 px-2">
                                  <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" title="Voir le document">
                                    <ExternalLink className="h-3.5 w-3.5" />
                                  </a>
                                </Button>
                              )}
                              {canAct && artisan?.User?.email && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2"
                                  title="Envoyer un rappel"
                                  disabled={sendEmailMutation.isPending}
                                  onClick={() => {
                                    sendEmailMutation.mutate({
                                      artisanProfileId: artisan.id,
                                      subject: `Rappel — Document ${docTypeLabel(doc.type)} à renouveler`,
                                      body: `Bonjour ${artisanName},\n\nVotre document "${docTypeLabel(doc.type)}" ${doc.expiresAt ? `expire le ${formatDate(doc.expiresAt)}` : "doit être mis à jour"}.\n\nMerci de le renouveler dans votre espace TravauxBTP.\n\nCordialement,\nL'équipe TravauxBTP`,
                                    });
                                  }}
                                >
                                  <Mail className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data && data.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <span className="text-sm text-muted-foreground">
                  Page {data.page} / {data.totalPages} — {data.total} documents
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="h-8"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page >= (data.totalPages || 1)}
                    onClick={() => setPage((p) => p + 1)}
                    className="h-8"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
